import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, isValidElement, useRef } from "react";
import { useTranslator } from "@templafy/translation";
import { useCombinedRefs } from "../../../hooks/useCombinedRefs.js";
import { useDevelopmentEffect } from "../../../hooks/_internal/useDevelopmentEffect.js";
import { IconLoading } from "../../../icons/IconLoading.js";
import { combineClasses } from "../../../utilities/combineClasses.js";
import { createComponentNamespace } from "../../../utilities/createComponentNamespace.js";
import BaseButton_module from "./BaseButton.module.js";
const BaseButton = /*#__PURE__*/ forwardRef(({ className, testId, variant = 'solid', action = 'secondary', theme = 'light', size = 'medium', alignToContent, loading, disabled, startAdornment, endAdornment, children, as, ...props }, ref)=>{
    const translator = useTranslator();
    const As = as ?? 'button';
    const buttonRef = useRef(null);
    const iconAdornments = [
        startAdornment,
        endAdornment
    ].filter((element)=>/*#__PURE__*/ isValidElement(element) && element.type === ButtonAdornmentIcon);
    const hasSingleIcon = 1 === iconAdornments.length && !children;
    useDevelopmentEffect(()=>{
        if (!buttonRef.current || props['aria-hidden']) return;
        if (!buttonRef.current.textContent && !props['aria-label'] && !props['aria-labelledby']) console.error('[BaseButton] A button must always have an accessible label. Provide either text content, the `aria-label` or the `aria-labelledby` attribute on the button element');
    }, [
        children,
        props['aria-label']
    ]);
    return /*#__PURE__*/ jsxs(As, {
        className: combineClasses(className, BaseButton_module.button, hasSingleIcon && BaseButton_module.iconButton),
        "data-variant": variant,
        "data-action": action,
        "data-theme": theme,
        "data-size": size,
        "data-testid": testId,
        "aria-label": loading ? translator.uiComponentsButtonLoadingLabel() : void 0,
        disabled: disabled || loading,
        "data-loading": loading,
        "data-align-to-content": alignToContent,
        ...'button' === As && {
            type: 'button'
        },
        ...props,
        ref: useCombinedRefs(ref, buttonRef),
        children: [
            /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    startAdornment,
                    children && /*#__PURE__*/ jsx("span", {
                        "data-testid": "text",
                        children: children
                    }),
                    endAdornment
                ]
            }),
            loading && /*#__PURE__*/ jsx("span", {
                className: BaseButton_module.loadingIcon,
                children: /*#__PURE__*/ jsx(ButtonAdornmentIcon, {
                    icon: IconLoading
                })
            })
        ]
    });
});
const ButtonAdornmentIcon = /*#__PURE__*/ forwardRef(({ icon: Icon, className, ...props }, ref)=>Icon ? /*#__PURE__*/ jsx(Icon, {
        "aria-hidden": true,
        className: combineClasses(className, BaseButton_module.buttonAdornmentIcon),
        ...props,
        ref: ref
    }) : null);
ButtonAdornmentIcon.displayName = 'ButtonAdornmentIcon';
const BaseButtonNamespace = createComponentNamespace({
    component: BaseButton,
    componentList: {
        ButtonAdornmentIcon
    },
    namespace: 'BaseButton'
});
BaseButtonNamespace.displayName = 'BaseButton';
export { BaseButtonNamespace as BaseButton, ButtonAdornmentIcon };
